/**
 * MobileLocationBar — Shared, clickable "Portland, OR, United States"
 * location strip used across the mobile FoodPulse views.
 *
 * Tapping the bar navigates the user to the location's geo hub page
 * (`/food-pulse/businesses?near=<city>,<state>`) where they can drill
 * into food businesses, farms, deals, and charities in that area.
 *
 * The current location is (for now) sourced from localStorage under
 * `fp_mobile_location` — the mobile onboarding flow already writes to
 * that key — falling back to a Portland default.
 */
import { MapPin, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const FP_PURPLE = "#653091";
const FP_GREEN = "#9CCA45";

function readStoredLocation() {
  try {
    const raw = localStorage.getItem("fp_mobile_location");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.city || parsed.state || parsed.country)) return parsed;
    }
  } catch { /* ignore malformed json */ }
  return { city: "Portland", state: "OR", country: "United States" };
}

export function useMobileLocation() {
  return readStoredLocation();
}

export default function MobileLocationBar({ testid = "home-location" }) {
  const loc = readStoredLocation();
  const label = [loc.city, loc.state, loc.country].filter(Boolean).join(", ");
  const nearParam = [loc.city, loc.state].filter(Boolean).join(",");
  const href = `/food-pulse/businesses${nearParam ? `?near=${encodeURIComponent(nearParam)}` : ""}`;
  return (
    <Link
      to={href}
      className="mt-5 flex items-center gap-1 px-4 active:opacity-70"
      data-testid={testid}
      aria-label={`Change location — currently ${label}`}
    >
      <MapPin className="w-4 h-4" style={{ color: FP_GREEN }} />
      <span
        className="text-base font-semibold underline decoration-dotted underline-offset-4"
        style={{ color: FP_PURPLE }}
      >
        {label}
      </span>
      <ChevronRight className="w-4 h-4 ml-0.5 opacity-70" style={{ color: FP_PURPLE }} />
    </Link>
  );
}
